import { Component, Input, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, Observable, of, Subject, zip } from 'rxjs';
import { catchError, debounceTime, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { UserApiService } from 'src/app/modules/management/store/users';
import { CommunicationsApiService } from 'src/app/modules/management/store/communication-center/communications-api.service';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import {
  Template,
  ChannelType,
  RenderedTemplate,
  Communication,
  CommunicationStatus,
  ConfiguredChannel
} from 'src/app/modules/management/store/communication-center';

@Component({
    selector: 'topo-recipient-select',
    templateUrl: './recipient-select.component.html',
    styleUrls: ['./recipient-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: RecipientSelectComponent
        }
    ],
    standalone: false
})
export class RecipientSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() placeholder: string;
  @Input() selectedRecipients: any[];
  @Input() channelType: ChannelType;
  @Output() selectedRecipientsChanged = new EventEmitter<any[]>();

  recipientsInput$ = new Subject<string>();
  destroy$: Subject<void> = new Subject<void>();
  users$ = new BehaviorSubject<any[]>([]);
  dist$ = new BehaviorSubject<any[]>([]);
  distListGroupName: string;
  userGroupName: string;
  recipientsLoading: boolean;
  recipients: any[];

  touched = false;
  @Input() disabled = false;
  onChange = value => {};
  onTouched = () => {};

  constructor(
    private store: Store<fromAuth.State>,
    private translate: TranslateService,
    private userApiService: UserApiService,
    private communicationsApiService: CommunicationsApiService
  ) {}

  ngOnInit(): void {
    this.translate.get(['MAP.DISTRIBUTION-LISTS', 'MAP.USERS']).subscribe(values => {
      this.distListGroupName = values['MAP.DISTRIBUTION-LISTS'];
      this.userGroupName = values['MAP.USERS'];
    });

    zip(this.users$, this.dist$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(([users, dists]) => {
        this.recipientsLoading = false;

        const values = [];

        values.push(
          ...users.map(u => {
            return {
              uId: u.uId,
              displayName: u.displayName,
              type: this.userGroupName
            };
          })
        );

        values.push(
          ...dists.map(d => {
            return {
              uId: d.uId,
              displayName: d.name,
              type: this.distListGroupName
            };
          })
        );

        this.recipients = values;
      });

    this.recipientsInput$.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(val => {
      this.searchForUsersAndDistributionLists(val);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  searchForUsersAndDistributionLists(searchTerm: string): void {
    this.getUsers(searchTerm)
      .pipe(take(1))
      .subscribe(users => {
        this.users$.next(users);
      });

    this.getDistributionLists(searchTerm)
      .pipe(take(1))
      .subscribe(dist => {
        this.dist$.next(dist);
      });
  }

  getUsers(searchTerm: string = null): Observable<any[]> {
    this.recipientsLoading = true;

    return this.store.select(fromAuth.selectUserContext).pipe(
      take(1),
      switchMap(userContext => {
        return this.userApiService
          .getUsers(userContext.organizationUId, {
            skip: 0,
            take: 20,
            sort: 'DisplayName',
            where: !!searchTerm ? `DisplayName.ToLower().Contains("${searchTerm.replace('"', '\\"')?.toLocaleLowerCase()}") AND IsServiceAccount==false` : 'IsServiceAccount==false'
          })
          .pipe(
            catchError(() => of({ data: [] })),
            map(({ data }) => {
              return data;
            })
          );
      })
    );
  }

  getDistributionLists(searchTerm: string = null): Observable<any[]> {
    this.recipientsLoading = true;

    let where = `ChannelType=ChannelType.${ChannelType[this.channelType]}`;
    where += !!searchTerm ? ` && Name.ToLower().Contains("${searchTerm.replace('"', '\\"')?.toLocaleLowerCase()}")` : '';

    return this.communicationsApiService
      .getDistributionLists({
        skip: 0,
        take: 20,
        sort: 'Name',
        where
      })
      .pipe(
        catchError(e => {
          console.error(e);
          return of({ data: [] });
        }),
        map(({ data }) => {
          return data;
        })
      );
  }

  addRecipient(event): void {
    this.selectedRecipientsChanged.emit(this.selectedRecipients);
    this.markAsTouched();
    this.onChange(this.selectedRecipients);
  }

  removeRecipient(event): void {
    this.selectedRecipientsChanged.emit(this.selectedRecipients);
    this.markAsTouched();
    this.onChange(this.selectedRecipients);
  }

  writeValue(selectedRecipients: any[]) {
    this.selectedRecipients = selectedRecipients;
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  trackByFn(val) {
    return val;
  }
}
